// ============================================================================
// KLINISOL CRO - Training API Service
// File: src/services/trainingApi.js
// Purpose: All API calls for training module functionality
// ============================================================================

import api from './api'; // Your existing axios instance

const trainingApi = {
    // ========================================================================
    // MODULE CONTENT
    // ========================================================================
    
    /**
     * Get complete module with all sections
     * @param {number} moduleId - Training module ID
     * @returns {Promise<Object>} Module data with sections
     */
    getModuleContent: async (moduleId) => {
        try {
            const response = await api.get(`/training/module/${moduleId}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching module content:', error);
            throw error;
        }
    },
    
    /**
     * Get specific section content
     * @param {number} moduleId - Module ID
     * @param {number} sectionId - Section ID
     * @returns {Promise<Object>} Section data
     */
    getSectionContent: async (moduleId, sectionId) => {
        try {
            const response = await api.get(`/training/module/${moduleId}/section/${sectionId}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching section content:', error);
            throw error;
        }
    },
    
    /**
     * Get module structure (metadata only)
     * @param {number} moduleId - Module ID
     * @returns {Promise<Object>} Module structure
     */
    getModuleStructure: async (moduleId) => {
        try {
            const response = await api.get(`/training/module/${moduleId}/structure`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching module structure:', error);
            throw error;
        }
    },
    
    /**
     * Start a training module
     * @param {number} moduleId - Module ID
     * @returns {Promise<Object>} Training record
     */
    startModule: async (moduleId) => {
        try {
            const response = await api.post(`/training/module/${moduleId}/start`);
            return response.data.data;
        } catch (error) {
            console.error('Error starting module:', error);
            throw error;
        }
    },
    
    // ========================================================================
    // PROGRESS TRACKING
    // ========================================================================
    
    /**
     * Get user's progress for a module
     * @param {number} moduleId - Module ID
     * @returns {Promise<Object>} Progress data
     */
    getUserProgress: async (moduleId) => {
        try {
            const response = await api.get(`/training/progress/${moduleId}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching progress:', error);
            throw error;
        }
    },
    
    /**
     * Start a section
     * @param {number} moduleId - Module ID
     * @param {number} sectionId - Section ID
     * @returns {Promise<Object>} Progress record
     */
    startSection: async (moduleId, sectionId) => {
        try {
            const response = await api.post('/training/progress/start', {
                moduleId,
                sectionId
            });
            return response.data.data;
        } catch (error) {
            console.error('Error starting section:', error);
            throw error;
        }
    },
    
    /**
     * Complete a section
     * @param {number} progressId - Progress record ID (or section ID if not started)
     * @param {number} timeSpent - Time spent in seconds
     * @returns {Promise<Object>} Updated progress
     */
    completeSection: async (progressId, timeSpent) => {
        try {
            const response = await api.post('/training/progress/complete', {
                progressId,
                timeSpent,
                lastPosition: {
                    completed: true,
                    timestamp: new Date().toISOString()
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error completing section:', error);
            throw error;
        }
    },
    
    /**
     * Update section progress (auto-save)
     * @param {number} progressId - Progress record ID
     * @param {Object} data - Update data {timeSpent, lastPosition, status}
     * @returns {Promise<Object>} Updated progress
     */
    updateProgress: async (progressId, data) => {
        try {
            const response = await api.put(`/training/progress/${progressId}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating progress:', error);
            throw error;
        }
    },
    
    /**
     * Get next available section
     * @param {number} moduleId - Module ID
     * @returns {Promise<Object>} Next section or null
     */
    getNextSection: async (moduleId) => {
        try {
            const response = await api.get(`/training/progress/${moduleId}/next-section`);
            return response.data.data;
        } catch (error) {
            console.error('Error getting next section:', error);
            throw error;
        }
    },
    
    /**
     * Get progress summary
     * @param {number} moduleId - Module ID
     * @returns {Promise<Object>} Progress summary
     */
    getProgressSummary: async (moduleId) => {
        try {
            const response = await api.get(`/training/progress/${moduleId}/summary`);
            return response.data.data;
        } catch (error) {
            console.error('Error getting progress summary:', error);
            throw error;
        }
    },
    
    // ========================================================================
    // QUIZ MANAGEMENT
    // ========================================================================
    
    /**
     * Get quiz questions (without answers)
     * @param {number} sectionId - Quiz section ID
     * @returns {Promise<Object>} Quiz data
     */
    getQuizQuestions: async (sectionId) => {
        try {
            const response = await api.get(`/training/quiz/${sectionId}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching quiz questions:', error);
            throw error;
        }
    },
    
    /**
     * Submit quiz attempt
     * @param {Object} data - {sectionId, moduleId, answers, startedAt, timeSpent}
     * @returns {Promise<Object>} Quiz results
     */
    submitQuiz: async (data) => {
        try {
            const response = await api.post('/training/quiz/submit', data);
            return response.data.data;
        } catch (error) {
            console.error('Error submitting quiz:', error);
            throw error;
        }
    },
    
    /**
     * Get quiz attempt results with review
     * @param {number} attemptId - Attempt ID
     * @returns {Promise<Object>} Attempt results with answers
     */
    getAttemptResults: async (attemptId) => {
        try {
            const response = await api.get(`/training/quiz/attempt/${attemptId}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching attempt results:', error);
            throw error;
        }
    },
    
    /**
     * Get all user's attempts for a quiz
     * @param {number} sectionId - Quiz section ID
     * @returns {Promise<Array>} List of attempts
     */
    getUserAttempts: async (sectionId) => {
        try {
            const response = await api.get(`/training/quiz/${sectionId}/attempts`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching user attempts:', error);
            throw error;
        }
    },
    
    /**
     * Get latest attempt for a quiz
     * @param {number} sectionId - Quiz section ID
     * @returns {Promise<Object>} Latest attempt
     */
    getLatestAttempt: async (sectionId) => {
        try {
            const response = await api.get(`/training/quiz/${sectionId}/latest-attempt`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching latest attempt:', error);
            throw error;
        }
    },
    
    /**
     * Check if user can attempt quiz
     * @param {number} sectionId - Quiz section ID
     * @returns {Promise<Object>} {canAttempt: boolean, reason: string}
     */
    canAttemptQuiz: async (sectionId) => {
        try {
            const response = await api.get(`/training/quiz/${sectionId}/can-attempt`);
            return response.data.data;
        } catch (error) {
            console.error('Error checking quiz attempt eligibility:', error);
            throw error;
        }
    },
    
    // ========================================================================
    // ELECTRONIC SIGNATURES
    // ========================================================================
    
    /**
     * Submit electronic signature
     * @param {Object} data - {trainingRecordId, signatureText, pin}
     * @returns {Promise<Object>} Signature record with certificate path
     */
    submitSignature: async (data) => {
        try {
            const response = await api.post('/training/signature', data);
            return response.data.data;
        } catch (error) {
            console.error('Error submitting signature:', error);
            throw error;
        }
    },
    
    /**
     * Verify signature authenticity
     * @param {number} signatureId - Signature ID
     * @returns {Promise<Object>} Signature verification data
     */
    verifySignature: async (signatureId) => {
        try {
            const response = await api.get(`/training/signature/${signatureId}/verify`);
            return response.data.data;
        } catch (error) {
            console.error('Error verifying signature:', error);
            throw error;
        }
    },
    
    /**
     * Get signature by training record ID
     * @param {number} recordId - Training record ID
     * @returns {Promise<Object>} Signature data
     */
    getSignatureByRecord: async (recordId) => {
        try {
            const response = await api.get(`/training/signature/record/${recordId}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching signature:', error);
            throw error;
        }
    },
    
    /**
     * Verify PIN before signature
     * @param {string} pin - User PIN
     * @returns {Promise<Object>} {valid: boolean}
     */
    verifyPIN: async (pin) => {
        try {
            const response = await api.post('/training/signature/verify-pin', { pin });
            return response.data.data;
        } catch (error) {
            console.error('Error verifying PIN:', error);
            throw error;
        }
    },
    
    /**
     * Check if user can sign training record
     * @param {number} recordId - Training record ID
     * @returns {Promise<Object>} {canSign: boolean, reason: string, completionPercentage: number}
     */
    canSignRecord: async (recordId) => {
        try {
            const response = await api.get(`/training/signature/record/${recordId}/can-sign`);
            return response.data.data;
        } catch (error) {
            console.error('Error checking sign eligibility:', error);
            throw error;
        }
    },
    
    /**
     * Download certificate PDF
     * @param {number} signatureId - Signature ID
     * @returns {string} Download URL
     */
    getCertificateDownloadUrl: (signatureId) => {
        return `/api/training/signature/${signatureId}/certificate`;
    }
};

export default trainingApi;